import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Check, Building2 } from 'lucide-react';
import ShelterCareConnectLogo from './ui/logo';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const Signup = () => {
  const { user, signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    shelter_name: '', admin_full_name: '', admin_username: '',
    admin_email: '', password: '', bed_count: '', billing_cycle: 'monthly'
  });
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/pricing`).then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  if (user) return <Navigate to="/onboarding" />;

  const tierForBeds = (beds) => {
    const b = parseInt(beds) || 0;
    if (!pricing) return null;
    const order = ['tier_50', 'tier_100', 'tier_150', 'tier_200', 'tier_250', 'tier_300'];
    for (const k of order) {
      if (b <= pricing.tiers[k].max_beds) return { key: k, ...pricing.tiers[k] };
    }
    return { key: 'tier_300', ...pricing.tiers.tier_300 };
  };

  const maxBeds = pricing?.max_self_serve_beds || 300;
  const overCap = (parseInt(form.bed_count) || 0) > maxBeds;
  const selectedTier = overCap ? null : tierForBeds(form.bed_count);
  const isBeta = pricing && pricing.beta_slots_remaining > 0;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const payload = { ...form, bed_count: form.bed_count ? parseInt(form.bed_count) : null };
    const result = await signup(payload);
    if (result.success) {
      toast.success('Welcome! Let\'s set up your shelter.');
      navigate('/onboarding');
    } else {
      setError(result.error);
      toast.error(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4" data-testid="signup-page">
      <div className="w-full max-w-4xl grid md:grid-cols-2 gap-6">
        {/* Left: value + pricing */}
        <div className="space-y-6 hidden md:block">
          <ShelterCareConnectLogo size="lg" showText={true} />
          <h1 className="text-3xl font-bold text-gray-900">Run your shelter, self-serve.</h1>
          <p className="text-gray-600">Set up in minutes. No sales call, no setup fees.</p>
          {pricing && (
            <div className="rounded-xl bg-white/70 border border-indigo-100 p-5 space-y-3">
              {isBeta ? (
                <div className="text-emerald-700 font-semibold flex items-center gap-2">
                  <Check className="h-5 w-5" /> Beta spot available — {pricing.beta_days} days free
                  <span className="text-xs text-gray-500">({pricing.beta_slots_remaining} of {pricing.beta_slots_total} left)</span>
                </div>
              ) : (
                <div className="text-indigo-700 font-semibold flex items-center gap-2">
                  <Check className="h-5 w-5" /> {pricing.trial_days}-day free trial
                </div>
              )}
              <div className="space-y-2 text-sm">
                {Object.entries(pricing.tiers).map(([k, t]) => (
                  <div key={k} className={`flex justify-between rounded-md px-3 py-2 ${selectedTier?.key === k ? 'bg-indigo-600 text-white' : 'bg-gray-50 text-gray-700'}`}>
                    <span>{t.name} · up to {t.max_beds} beds</span>
                    <span className="font-medium">${t.monthly}/mo</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">No card required to start. Cancel anytime.</p>
            </div>
          )}
        </div>

        {/* Right: form */}
        <Card className="card-shadow-lg border-0 bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-indigo-600" /> Create your shelter account</CardTitle>
            <CardDescription>
              {overCap
                ? '300+ beds — let\'s set up a custom plan for you'
                : selectedTier ? `${selectedTier.name} plan · $${selectedTier.monthly}/mo · start with a ${pricing?.trial_days || 30}-day free trial` : 'Enter your bed count to see your plan'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-700">{error}</AlertDescription>
                </Alert>
              )}
              <Input name="shelter_name" required placeholder="Shelter name" value={form.shelter_name} onChange={handleChange} data-testid="signup-shelter-name" />
              <Input name="bed_count" type="number" min="1" placeholder="Number of beds (e.g. 40)" value={form.bed_count} onChange={handleChange} data-testid="signup-bed-count" />
              <Input name="admin_full_name" required placeholder="Your full name" value={form.admin_full_name} onChange={handleChange} data-testid="signup-full-name" />
              <Input name="admin_username" required placeholder="Choose a username" value={form.admin_username} onChange={handleChange} data-testid="signup-username" />
              <Input name="admin_email" type="email" required placeholder="Email address" value={form.admin_email} onChange={handleChange} data-testid="signup-email" />
              <Input name="password" type="password" required minLength={6} placeholder="Create a password" value={form.password} onChange={handleChange} data-testid="signup-password" />
              <div className="flex gap-2">
                {['monthly', 'annual'].map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, billing_cycle: c })}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm capitalize ${form.billing_cycle === c ? 'border-indigo-600 bg-indigo-50 text-indigo-700 font-medium' : 'border-gray-200 text-gray-600'}`}
                    data-testid={`signup-billing-${c}`}>
                    {c}{c === 'annual' ? ' (2 months free)' : ''}
                  </button>
                ))}
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="signup-submit-btn">
                {loading ? 'Creating account...' : 'Start free'}
              </Button>
              {overCap && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="signup-enterprise-note">
                  Shelters with more than {maxBeds} beds get a custom plan. You can still create your account now — our team will reach out to tailor pricing.
                </p>
              )}
            </form>
            <div className="mt-4 text-center text-sm text-gray-600">
              Already have an account?{' '}
              <button type="button" onClick={() => navigate('/login')} className="text-indigo-600 font-medium hover:underline" data-testid="go-to-login-link">Sign in</button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Signup;
